import React, { useState, useEffect, useMemo, useRef } from 'react'
import { api, apiClient } from '../lib/api'
import { isAxiosError } from 'axios'
import { BarChart3, Users, BookOpen, TrendingUp, FileDown, LineChart as LineChartIcon, CalendarDays } from 'lucide-react'
import ActivityHeatmap from '../components/ActivityHeatmap'
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils'
import EmailCredentialModal from '../components/EmailCredentialModal'
import PersonalDataRequiredModal from '../components/PersonalDataRequiredModal'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useResolvedDarkMode } from '../hooks/useResolvedDarkMode'
import {
  studentAssessmentsApi,
  StudentAssessmentAlertLevel,
  StudentAssessmentAutomationConfig,
  StudentAssessmentConfigPayload
} from '../lib/studentAssessmentsApi'

interface Class {
  Klasse: string
}

interface Student {
  ID: string
  Name: string
  Vorname: string
  Klasse: string
}

interface LearningPlanProgress {
  fach: string
  learningPlansCount: number
  totalTasks: number
  completedTasks: number
  progress: number
  // Neue Felder für Zusatzaufgaben-Statistiken
  regularTasks: number
  completedRegularTasks: number
  baseProgress: number
  bonusTasks: number
  completedBonusTasks: number
  bonusProgress: number
}

interface KIRecommendation {
  Unid: string
  Datum: string
  S?: string
  Memo?: string
}

interface StudentProgress {
  student: Student
  progress: {
    Deutsch: LearningPlanProgress
    Mathematik: LearningPlanProgress
    Englisch: LearningPlanProgress
    Gesamt: LearningPlanProgress
  }
  kiRecommendations?: KIRecommendation[]
}

type ProgressSubject = 'Deutsch' | 'Mathematik' | 'Englisch' | 'Gesamt'

interface SubjectTaskItem {
  aid: number
  lpid: number
  kurzbezeichnung: string
  sortierung: number
  zusatzaufgabe: boolean
  fach: string
  lernplanName: string
  completed: boolean
}

interface SubjectTasksPayload {
  subject: ProgressSubject
  tasks: SubjectTaskItem[]
  completedCount: number
  totalCount: number
}

interface SubjectTasksModalState {
  mode: 'hover' | 'click'
  student: Student
  subject: ProgressSubject
  anchorTop?: number
  anchorLeft?: number
}

type EvidenceTrendSubject = 'ALL' | 'Deutsch' | 'Mathematik' | 'Englisch'

interface EvidenceTrendSeries {
  key: string
  label: string
  type: 'student' | 'classAverage'
  studentId?: string
}

interface EvidenceTrendSummary {
  totalBelege: number
  classAverageTotal: number | null
  studentCount: number
  selectedStudents: Array<{
    studentId: string
    label: string
    totalBelege: number
  }>
}

interface EvidenceTrendData {
  range: {
    startDate: string
    endDate: string
    totalDays: number
  }
  subject: string
  series: EvidenceTrendSeries[]
  timeline: Array<Record<string, number | string>>
  summary: EvidenceTrendSummary
}

interface EvidenceTodayStudentEntry {
  studentId: string
  label: string
  totalBelege: number
  deutschBelege: number
  mathematikBelege: number
  englischBelege: number
}

interface EvidenceTodayData {
  date: string
  totalStudents: number
  studentsWithBelege: number
  totalBelege: number
  students: EvidenceTodayStudentEntry[]
}

type EvidencePanelMode = 'trend' | 'today'

interface TrendRangeResolution {
  start: string
  end: string
  isValid: boolean
  reason: 'missing' | null
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const serverMessage = (error.response?.data as any)?.error
    return serverMessage || error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

const MAX_SERIES_STYLES = 40

function getCurrentSchoolYear() {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  if (currentMonth >= 8) {
    return {
      start: new Date(currentYear, 7, 1),
      end: new Date(currentYear + 1, 6, 31),
      label: `${currentYear}/${currentYear + 1}`
    }
  }

  return {
    start: new Date(currentYear - 1, 7, 1),
    end: new Date(currentYear, 6, 31),
    label: `${currentYear - 1}/${currentYear}`
  }
}

const Statistics: React.FC = () => {
  const { user } = useAuth()
  const isDark = useResolvedDarkMode()
  const chartAxisColor = isDark ? '#9ca3af' : '#6b7280'
  const chartGridColor = isDark ? '#374151' : '#f3f4f6'
  const [classes, setClasses] = useState<Class[]>([])
  const [selectedClass, setSelectedClass] = useState<string>('')
  const [studentProgress, setStudentProgress] = useState<StudentProgress[]>([])
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [useDateRange, setUseDateRange] = useState(false)
  const [sortBy, setSortBy] = useState<'name' | 'deutsch' | 'mathematik' | 'englisch' | 'gesamt'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [selectedStudentInfo, setSelectedStudentInfo] = useState<StudentProgress | null>(null)
  const [showStudentModal, setShowStudentModal] = useState(false)
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set())
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({})

  const [evidencePanelMode, setEvidencePanelMode] = useState<EvidencePanelMode>('trend')
  const [evidenceTrendSubject, setEvidenceTrendSubject] = useState<EvidenceTrendSubject>('ALL')
  const [includeClassAverage, setIncludeClassAverage] = useState(true)
  const [evidenceTrendData, setEvidenceTrendData] = useState<EvidenceTrendData | null>(null)
  const [evidenceTrendLoading, setEvidenceTrendLoading] = useState(false)
  const [evidenceTrendError, setEvidenceTrendError] = useState<string | null>(null)
  const [evidenceTodayData, setEvidenceTodayData] = useState<EvidenceTodayData | null>(null)
  const [evidenceTodayLoading, setEvidenceTodayLoading] = useState(false)
  const [evidenceTodayError, setEvidenceTodayError] = useState<string | null>(null)
  const [showEmailCredentialModal, setShowEmailCredentialModal] = useState(false)
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false)
  const [pendingSendKind, setPendingSendKind] = useState<null | 'raw' | 'interpreted'>(null)
  const [assessmentConfigs, setAssessmentConfigs] = useState<Record<string, StudentAssessmentAutomationConfig>>({})
  const [assessmentConfigDraft, setAssessmentConfigDraft] = useState<{
    enabled: boolean
    minAlertLevel: StudentAssessmentAlertLevel
    eventCooldownMinutes: number
  }>({
    enabled: false,
    minAlertLevel: 'yellow',
    eventCooldownMinutes: 240
  })
  const [isAssessmentConfigDirty, setIsAssessmentConfigDirty] = useState(false)
  const [assessmentConfigLoading, setAssessmentConfigLoading] = useState(false)
  const [assessmentConfigSaving, setAssessmentConfigSaving] = useState(false)
  const [assessmentRunBusy, setAssessmentRunBusy] = useState(false)
  const [subjectTasksModal, setSubjectTasksModal] = useState<SubjectTasksModalState | null>(null)
  const [subjectTasksCache, setSubjectTasksCache] = useState<Record<string, SubjectTasksPayload>>({})
  const [subjectTasksLoadingKey, setSubjectTasksLoadingKey] = useState<string | null>(null)
  const [subjectTasksError, setSubjectTasksError] = useState<string | null>(null)
  const [subjectTaskToggleBusyAid, setSubjectTaskToggleBusyAid] = useState<number | null>(null)
  const hoverPreviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Aktive Ansicht (Tabs)
  type StatView = 'gauges' | 'trend' | 'heatmap'
  const [activeView, setActiveView] = useState<StatView>('gauges')

  // Heatmap (Panel D)

  const [heatmapSubject, setHeatmapSubject] = useState<'ALL' | 'Deutsch' | 'Mathematik' | 'Englisch'>('ALL')
  const [heatmapData, setHeatmapData] = useState<{
    rangeStart: string
    rangeEnd: string
    subject: string
    students: Array<{ id: string; label: string; dailyCounts: Record<string, number>; topTeachers?: Array<{ teacher: string; count: number }> }>
    classDailyTotals: Record<string, number>
    classAverageDailyTotals: Record<string, number>
    classTopTeachers?: Array<{ teacher: string; count: number }>
  } | null>(null)
  const [heatmapLoading, setHeatmapLoading] = useState(false)
  const [heatmapError, setHeatmapError] = useState<string | null>(null)

  const ensureEmailPreflight = async (kind: 'raw' | 'interpreted'): Promise<boolean> => {
    setPendingSendKind(kind)
    // 1) Personal data (sender name)
    try {
      const profRes = await api.get('/profile')
      const p = profRes.data?.data
      const fn = (p?.firstName || '').toString().trim()
      const ln = (p?.lastName || '').toString().trim()
      const dn = (p?.displayName || '').toString().trim()
      const hasName = Boolean(`${fn} ${ln}`.trim()) || Boolean(dn)
      if (!hasName) {
        setShowPersonalDataModal(true)
        return false
      }
    } catch {
      // Non-blocking: backend can fallback to username
    }

    // 2) Email credentials
    try {
      const credRes = await api.get('/email/credentials')
      const creds = credRes.data?.data || []
      const hasEnabled = creds.some((c: any) => c.enabled)
      if (!hasEnabled) {
        setShowEmailCredentialModal(true)
        return false
      }
    } catch {
      toast.error('E-Mail-Konten konnten nicht geprüft werden')
      return false
    }

    setPendingSendKind(null)
    return true
  }

  // SessionStorage-Keys
  const SESSION_KEYS = {
    selectedClass: 'statistics_selectedClass',
    startDate: 'statistics_startDate',
    endDate: 'statistics_endDate',
    useDateRange: 'statistics_useDateRange',
    sortBy: 'statistics_sortBy',
    sortOrder: 'statistics_sortOrder'
  }
  const assessmentAlertLevelOptions: Array<{ value: StudentAssessmentAlertLevel; label: string }> = [
    { value: 'red', label: 'Nur Rot' },
    { value: 'yellow', label: 'Ab Gelb' },
    { value: 'green', label: 'Ab Grün' },
    { value: 'gray', label: 'Alles inkl. Grau' }
  ]
  const collectedRoles = new Set<string>()
  if (user?.role) collectedRoles.add(String(user.role))
  ;(user?.roles || []).forEach((role) => role && collectedRoles.add(String(role)))
  ;(user?.allRoles || []).forEach((role) => role && collectedRoles.add(String(role)))
  const normalizedRoles = Array.from(collectedRoles).map((role) => role.toLowerCase())
  const canUseStudentAssessments = normalizedRoles.some((role) =>
    ['teacher', 'lehrer', 'klassenlehrer', 'abteilungsleiter', 'schulleitung', 'admin', 'administrator'].includes(role)
  )

  const applyAssessmentConfigToDraft = (config: StudentAssessmentAutomationConfig | null) => {
    const nextDraft = {
      enabled: config?.enabled ?? false,
      minAlertLevel: (config?.minAlertLevel || 'yellow') as StudentAssessmentAlertLevel,
      eventCooldownMinutes: config?.eventCooldownMinutes ?? 240
    }
    setAssessmentConfigDraft(nextDraft)
    setIsAssessmentConfigDirty(false)
  }

  const fetchAssessmentConfigs = async (silent = false) => {
    if (!canUseStudentAssessments) return
    if (!silent) setAssessmentConfigLoading(true)
    try {
      const configs = await studentAssessmentsApi.getAutomationConfigs()
      const map: Record<string, StudentAssessmentAutomationConfig> = {}
      configs.forEach((config) => {
        map[config.className] = config
      })
      setAssessmentConfigs(map)
      if (selectedClass) {
        applyAssessmentConfigToDraft(map[selectedClass] || null)
      }
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        toast.error('Keine Berechtigung für Lernstand-Automatik in dieser Rolle.')
      } else {
        toast.error('Lernstand-Konfiguration konnte nicht geladen werden.')
      }
    } finally {
      if (!silent) setAssessmentConfigLoading(false)
    }
  }

  const handleSaveAssessmentClassConfig = async () => {
    if (!selectedClass) {
      toast.error('Bitte zuerst eine Klasse auswählen.')
      return
    }
    const cooldown = Math.max(15, Number(assessmentConfigDraft.eventCooldownMinutes) || 240)
    const payload: StudentAssessmentConfigPayload = {
      enabled: assessmentConfigDraft.enabled,
      minAlertLevel: assessmentConfigDraft.minAlertLevel,
      eventCooldownMinutes: cooldown
    }
    setAssessmentConfigSaving(true)
    try {
      const saved = await studentAssessmentsApi.saveAutomationConfig(selectedClass, payload)
      setAssessmentConfigs((prev) => ({ ...prev, [selectedClass]: saved }))
      applyAssessmentConfigToDraft(saved)
      toast.success(`Lernstand-Konfiguration für ${selectedClass} gespeichert.`)
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        toast.error('Speichern nicht erlaubt: fehlende Klassenberechtigung.')
      } else {
        toast.error(error?.response?.data?.error || 'Speichern der Lernstand-Konfiguration fehlgeschlagen.')
      }
    } finally {
      setAssessmentConfigSaving(false)
    }
  }

  const handleRunAssessmentForSelectedStudents = async () => {
    if (!selectedClass) {
      toast.error('Bitte zuerst eine Klasse auswählen.')
      return
    }
    const studentIds = Array.from(selectedStudents)
    if (studentIds.length === 0) {
      toast.error('Bitte mindestens einen Schüler auswählen.')
      return
    }
    setAssessmentRunBusy(true)
    try {
      const results = await Promise.allSettled(studentIds.map((unid) => studentAssessmentsApi.runStudent(unid, 60)))
      const success = results.filter((result) => result.status === 'fulfilled').length
      const failed = results.length - success
      if (success > 0) {
        toast.success(`Lernstand-Läufe gestartet: ${success} Job(s).`)
      }
      if (failed > 0) {
        toast.error(`${failed} Lauf/Läufe konnten nicht gestartet werden.`)
      }
    } catch {
      toast.error('Manueller Schülerlauf konnte nicht gestartet werden.')
    } finally {
      setAssessmentRunBusy(false)
    }
  }

  const handleRunAssessmentForClass = async () => {
    if (!selectedClass) {
      toast.error('Bitte zuerst eine Klasse auswählen.')
      return
    }
    setAssessmentRunBusy(true)
    try {
      const result = await studentAssessmentsApi.runClass(selectedClass, 60, 35)
      toast.success(`Klassenlauf gestartet: ${result.queuedStudents} Schüler in Queue.`)
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        toast.error('Klassenlauf nicht erlaubt: fehlende Berechtigung.')
      } else {
        toast.error(error?.response?.data?.error || 'Klassenlauf konnte nicht gestartet werden.')
      }
    } finally {
      setAssessmentRunBusy(false)
    }
  }

  // Lade Zustand aus SessionStorage beim Komponenten-Mount
  useEffect(() => {
    const savedSelectedClass = sessionStorage.getItem(SESSION_KEYS.selectedClass)
    const savedStartDate = sessionStorage.getItem(SESSION_KEYS.startDate)
    const savedEndDate = sessionStorage.getItem(SESSION_KEYS.endDate)
    const savedUseDateRange = sessionStorage.getItem(SESSION_KEYS.useDateRange)
    const savedSortBy = sessionStorage.getItem(SESSION_KEYS.sortBy)
    const savedSortOrder = sessionStorage.getItem(SESSION_KEYS.sortOrder)

    if (savedSelectedClass) setSelectedClass(savedSelectedClass)
    if (savedStartDate) setStartDate(savedStartDate)
    if (savedEndDate) setEndDate(savedEndDate)
    if (savedUseDateRange) setUseDateRange(savedUseDateRange === 'true')
    if (savedSortBy) setSortBy(savedSortBy as 'name' | 'deutsch' | 'mathematik' | 'englisch' | 'gesamt')
    if (savedSortOrder) setSortOrder(savedSortOrder as 'asc' | 'desc')
  }, [])

  // Speichere Zustand in SessionStorage bei Änderungen
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.selectedClass, selectedClass)
  }, [selectedClass])

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.startDate, startDate)
  }, [startDate])

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.endDate, endDate)
  }, [endDate])

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.useDateRange, useDateRange.toString())
  }, [useDateRange])

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.sortBy, sortBy)
  }, [sortBy])

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.sortOrder, sortOrder)
  }, [sortOrder])

  // Schuljahr-Logik: 1. August bis 31. Juli
  const schoolYear = useMemo(() => getCurrentSchoolYear(), [])
  const minDate = schoolYear.start.toISOString().split('T')[0]
  const maxDate = schoolYear.end.toISOString().split('T')[0]
  const selectedChartStudentIds = useMemo(
    () => Array.from(selectedStudents).sort((a, b) => a.localeCompare(b)),
    [selectedStudents]
  )
  const selectedChartStudentIdsKey = selectedChartStudentIds.join(',')
  const resolvedTrendRange = useMemo<TrendRangeResolution>(() => {
    const fallbackStart = schoolYear.start.toISOString().split('T')[0]
    const today = new Date()
    const cappedEndDate = today > schoolYear.end ? schoolYear.end : today
    const fallbackEnd = cappedEndDate.toISOString().split('T')[0]

    if (useDateRange) {
      if (!startDate || !endDate) {
        return { start: '', end: '', isValid: false, reason: 'missing' }
      }
      return { start: startDate, end: endDate, isValid: true, reason: null }
    }

    return { start: fallbackStart, end: fallbackEnd, isValid: true, reason: null }
  }, [useDateRange, startDate, endDate, schoolYear])
  const evidenceNumberFormatter = useMemo(
    () => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }),
    []
  )
  const trendRangeLabel = useMemo(() => {
    const fallbackLabel =
      resolvedTrendRange.start && resolvedTrendRange.end
        ? `${new Date(resolvedTrendRange.start).toLocaleDateString('de-DE')} – ${new Date(
          resolvedTrendRange.end
        ).toLocaleDateString('de-DE')}`
        : 'Zeitraum wählen'
    if (evidenceTrendData?.range) {
      return `${new Date(evidenceTrendData.range.startDate).toLocaleDateString('de-DE')} – ${new Date(
        evidenceTrendData.range.endDate
      ).toLocaleDateString('de-DE')}`
    }
    return fallbackLabel
  }, [evidenceTrendData, resolvedTrendRange])
  const generateColorPalette = (count: number) => {
    const maxCount = Math.max(1, Math.min(count, MAX_SERIES_STYLES))
    const colors: string[] = []
    for (let i = 0; i < maxCount; i += 1) {
      const hue = Math.round((360 / maxCount) * i)
      const saturation = 65 + ((i % 3) * 5)
      const lightness = 45 + ((i % 4) * 5)
      colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`)
    }
    return colors
  }
  const evidenceSeriesStyles = useMemo(() => {
    if (!evidenceTrendData?.series?.length) {
      return {}
    }
    const dashVariants: Array<string | undefined> = [undefined, '6 3', '2 2', '8 4 2 4', '1 4']
    const palette = generateColorPalette(evidenceTrendData.series.length)
    const paletteLength = palette.length
    const styleMap: Record<string, { color: string; dash?: string; strokeWidth: number }> = {}

    evidenceTrendData.series.forEach((series, index) => {
      if (series.type === 'classAverage') {
        styleMap[series.key] = {
          color: '#111827',
          dash: '4 4',
          strokeWidth: 3
        }
        return
      }

      const color = palette[index % paletteLength]
      const dashVariantIndex = paletteLength >= evidenceTrendData.series.length
        ? 0
        : Math.floor(index / paletteLength) % dashVariants.length

      styleMap[series.key] = {
        color,
        dash: dashVariants[dashVariantIndex],
        strokeWidth: 2
      }
    })

    return styleMap
  }, [evidenceTrendData])
  const evidenceTrendSubjectLabels: Record<EvidenceTrendSubject | 'ALL', string> = {
    ALL: 'Alle Fächer',
    Deutsch: 'Deutsch',
    Mathematik: 'Mathematik',
    Englisch: 'Englisch'
  }

  // Handler für Checkbox-Auswahl
  const handleStudentSelection = (studentId: string, isSelected: boolean) => {
    console.log('🔘 [FRONTEND] Student selection changed:', { studentId, isSelected })
    setSelectedStudents(prev => {
      const newSet = new Set(prev)
      if (isSelected) {
        newSet.add(studentId)
      } else {
        newSet.delete(studentId)
      }
      console.log('🔘 [FRONTEND] Updated selected students:', Array.from(newSet))
      return newSet
    })
  }

  // Fotos für Schüler laden (in Batches für bessere Performance)
  const loadAllStudentPhotos = async (students: Student[]) => {
    try {
      const sophomorixUnids = students.map(s => s.ID);
      if (sophomorixUnids.length === 0) return;
      
      console.log(`📸 Lade Fotos für ${sophomorixUnids.length} Schüler in Statistik...`);
      
      // Fotos in Batches von 20 laden für bessere Performance
      const batchSize = 20;
      const batches = [];
      for (let i = 0; i < sophomorixUnids.length; i += batchSize) {
        batches.push(sophomorixUnids.slice(i, i + batchSize));
      }
      
      const allPhotos: Record<string, string | null> = {};
      
      for (const batch of batches) {
        try {
          const photos = await loadStudentPhotos(batch);
          Object.assign(allPhotos, photos);
          
          // Kurze Pause zwischen Batches um Server nicht zu überlasten
          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.warn('Fehler beim Laden eines Foto-Batches:', error);
          // Setze null für alle IDs in diesem Batch
          batch.forEach(id => {
            allPhotos[id] = null;
          });
        }
      }
      
      console.log(`✅ ${Object.values(allPhotos).filter(p => p !== null).length} Fotos geladen`);
      setStudentPhotos(allPhotos);
    } catch (error) {
      console.warn('Fehler beim Laden der Schülerfotos:', error);
    }
  };

  const getSubjectTasksCacheKey = (studentId: string, subject: ProgressSubject) => `${studentId}:${subject}`

  const clearHoverPreviewTimer = () => {
    if (hoverPreviewTimerRef.current) {
      clearTimeout(hoverPreviewTimerRef.current)
      hoverPreviewTimerRef.current = null
    }
  }

  const closeSubjectTasksModal = () => {
    clearHoverPreviewTimer()
    setSubjectTasksModal(null)
    setSubjectTasksError(null)
    setSubjectTasksLoadingKey(null)
    setSubjectTaskToggleBusyAid(null)
  }

  const buildProgressQueryUrl = () => {
    const params = new URLSearchParams()
    if (useDateRange && startDate && endDate) {
      params.append('startDate', startDate)
      params.append('endDate', endDate)
    }

    const queryString = params.toString()
    return `/statistics/class/${selectedClass}/progress${queryString ? `?${queryString}` : ''}`
  }

  const refreshStudentProgress = async () => {
    if (!selectedClass) {
      setStudentProgress([])
      return
    }

    setLoading(true)
    try {
      const response = await apiClient.get(buildProgressQueryUrl())
      if (response.data.success) {
        const studentProgressData = response.data.data
        setStudentProgress(studentProgressData)
        const students = studentProgressData.map((sp: any) => sp.student)
        await loadAllStudentPhotos(students)
      }
    } catch (error) {
      console.error('Fehler beim Laden des Lernplan-Fortschritts:', error)
      setStudentProgress([])
    } finally {
      setLoading(false)
    }
  }

  const loadSubjectTasks = async (
    studentId: string,
    subject: ProgressSubject,
    options?: { force?: boolean }
  ) => {
    const cacheKey = getSubjectTasksCacheKey(studentId, subject)
    if (!options?.force && subjectTasksCache[cacheKey]) {
      return subjectTasksCache[cacheKey]
    }

    setSubjectTasksLoadingKey(cacheKey)
    setSubjectTasksError(null)

    try {
      const params = new URLSearchParams()
      if (useDateRange && startDate && endDate) {
        params.append('startDate', startDate)
        params.append('endDate', endDate)
      }

      const queryString = params.toString()
      const url = `/statistics/student/${studentId}/subject/${encodeURIComponent(subject)}/tasks${queryString ? `?${queryString}` : ''}`
      const response = await apiClient.get(url)
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Aufgaben konnten nicht geladen werden')
      }

      const payload: SubjectTasksPayload = {
        subject,
        tasks: response.data.data.tasks || [],
        completedCount: response.data.data.completedCount || 0,
        totalCount: response.data.data.totalCount || 0
      }

      setSubjectTasksCache((prev) => ({ ...prev, [cacheKey]: payload }))
      return payload
    } catch (error) {
      const message = getErrorMessage(error)
      setSubjectTasksError(message)
      throw error
    } finally {
      setSubjectTasksLoadingKey((current) => (current === cacheKey ? null : current))
    }
  }

  const handleGaugeMouseEnter = (
    student: Student,
    subject: ProgressSubject,
    target: HTMLDivElement
  ) => {
    clearHoverPreviewTimer()
    const cacheKey = getSubjectTasksCacheKey(student.ID, subject)
    hoverPreviewTimerRef.current = setTimeout(async () => {
      try {
        await loadSubjectTasks(student.ID, subject)
        const rect = target.getBoundingClientRect()
        setSubjectTasksModal({
          mode: 'hover',
          student,
          subject,
          anchorTop: rect.bottom + 10,
          anchorLeft: rect.left + rect.width / 2
        })
      } catch {
        setSubjectTasksModal({
          mode: 'hover',
          student,
          subject,
          anchorTop: target.getBoundingClientRect().bottom + 10,
          anchorLeft: target.getBoundingClientRect().left + target.getBoundingClientRect().width / 2
        })
      }
      setSubjectTasksLoadingKey((current) => (current === cacheKey ? null : current))
      hoverPreviewTimerRef.current = null
    }, 300)
  }

  const handleGaugeMouseMove = (studentId: string, subject: ProgressSubject) => {
    const cacheKey = getSubjectTasksCacheKey(studentId, subject)
    clearHoverPreviewTimer()
    setSubjectTasksModal((current) => {
      if (current?.mode === 'hover' && getSubjectTasksCacheKey(current.student.ID, current.subject) === cacheKey) {
        return null
      }
      return current
    })
  }

  const handleGaugeMouseLeave = (studentId: string, subject: ProgressSubject) => {
    const cacheKey = getSubjectTasksCacheKey(studentId, subject)
    clearHoverPreviewTimer()
    setSubjectTasksModal((current) => {
      if (current?.mode === 'hover' && getSubjectTasksCacheKey(current.student.ID, current.subject) === cacheKey) {
        return null
      }
      return current
    })
  }

  const handleGaugeClick = async (
    student: Student,
    subject: ProgressSubject
  ) => {
    clearHoverPreviewTimer()
    setSubjectTasksError(null)
    setSubjectTasksModal({
      mode: 'click',
      student,
      subject
    })

    try {
      await loadSubjectTasks(student.ID, subject)
    } catch {
      // Fehler wird über subjectTasksError angezeigt
    }
  }

  const handleSubjectTaskToggle = async (task: SubjectTaskItem) => {
    if (!subjectTasksModal || subjectTasksModal.mode !== 'click') {
      return
    }

    const cacheKey = getSubjectTasksCacheKey(subjectTasksModal.student.ID, subjectTasksModal.subject)
    setSubjectTaskToggleBusyAid(task.aid)
    setSubjectTasksError(null)

    try {
      if (task.completed) {
        await api.delete(`/students/${subjectTasksModal.student.ID}/tasks/complete`, {
          data: { aid: task.aid }
        })
      } else {
        await api.post(`/students/${subjectTasksModal.student.ID}/tasks/complete`, {
          aid: task.aid,
          lpid: task.lpid
        })
      }

      setSubjectTasksCache((prev) => {
        const existing = prev[cacheKey]
        if (!existing) {
          return prev
        }
        const updatedTasks = existing.tasks.map((item) =>
          item.aid === task.aid ? { ...item, completed: !item.completed } : item
        )
        const completedCount = updatedTasks.reduce((count, item) => count + (item.completed ? 1 : 0), 0)
        return {
          ...prev,
          [cacheKey]: {
            ...existing,
            tasks: updatedTasks,
            completedCount
          }
        }
      })

      await refreshStudentProgress()
    } catch (error) {
      setSubjectTasksError(getErrorMessage(error))
    } finally {
      setSubjectTaskToggleBusyAid(null)
    }
  }

  // Handler für "Alle auswählen"
  const handleSelectAll = () => {
    const allStudentIds = studentProgress.map(sp => sp.student.ID)
    setSelectedStudents(new Set(allStudentIds))
  }

  // Handler für "Alle abwählen"
  const handleDeselectAll = () => {
    setSelectedStudents(new Set())
  }

  // Handler für E-Mail-Versand (Reine Statistik)
  const handleSendRawStatistics = async () => {
    if (selectedStudents.size === 0) {
      alert('Bitte wählen Sie mindestens einen Schüler aus.')
      return
    }

    if (!(await ensureEmailPreflight('raw'))) {
      return
    }
    
    try {
      const studentIds = Array.from(selectedStudents)
      const dateRange = useDateRange && startDate && endDate ? { startDate, endDate } : null
      
      console.log('📧 [FRONTEND] Sending raw statistics:', {
        studentIds,
        classId: selectedClass,
        dateRange,
        selectedStudentsSize: selectedStudents.size
      })
      
      const response = await apiClient.post('/statistics/send-raw-statistics', {
        studentIds,
        classId: selectedClass,
        dateRange
      })
      
      if (response.data.success) {
        alert(`Reine Statistik erfolgreich an Eltern von ${response.data.results.length} Schülern gesendet.`)
        if (response.data.errors && response.data.errors.length > 0) {
          console.warn('Some errors occurred:', response.data.errors)
        }
      } else {
        alert(`Fehler: ${response.data.error}`)
      }
    } catch (error) {
      console.error('Error sending raw statistics:', error)
      alert(`Fehler beim Senden der reinen Statistik: ${getErrorMessage(error)}`)
    }
  }

  // Handler für E-Mail-Versand (Interpretierte Statistik) - Job-Queue Version
  const handleSendInterpretedStatistics = async () => {
    if (selectedStudents.size === 0) {
      alert('Bitte wählen Sie mindestens einen Schüler aus.')
      return
    }

    if (!(await ensureEmailPreflight('interpreted'))) {
      return
    }
    
    try {
      const studentIds = Array.from(selectedStudents)
      const dateRange = useDateRange && startDate && endDate ? { startDate, endDate } : null
      
      console.log('📧 [FRONTEND] Creating email job for interpreted statistics:', {
        studentIds,
        classId: selectedClass,
        dateRange,
        selectedStudentsSize: selectedStudents.size
      })
      
      const response = await apiClient.post('/email-jobs/send-interpreted-statistics', {
        studentIds,
        classId: selectedClass,
        dateRange
      })
      
      if (response.data.success) {
        alert(`E-Mails werden im Hintergrund versendet.\nGeschätzte Zeit: ${response.data.estimatedTime}\nJob-ID: ${response.data.jobId}`)
        
        // Optional: Job-Status überwachen (könnte in einem Modal oder Toast angezeigt werden)
        console.log('📧 [FRONTEND] Email job created:', response.data.jobId)
      } else {
        alert(`Fehler: ${response.data.error}`)
      }
    } catch (error) {
      console.error('Error creating email job:', error)
      alert(`Fehler beim Erstellen des E-Mail-Jobs: ${getErrorMessage(error)}`)
    }
  }

  // Sortierungsfunktion
  const sortStudentProgress = (progress: StudentProgress[]) => {
    return [...progress].sort((a, b) => {
      let valueA: number | string
      let valueB: number | string

      switch (sortBy) {
        case 'name':
          valueA = a.student.Name.toLowerCase()
          valueB = b.student.Name.toLowerCase()
          break
        case 'deutsch':
          valueA = a.progress.Deutsch.progress
          valueB = b.progress.Deutsch.progress
          break
        case 'mathematik':
          valueA = a.progress.Mathematik.progress
          valueB = b.progress.Mathematik.progress
          break
        case 'englisch':
          valueA = a.progress.Englisch.progress
          valueB = b.progress.Englisch.progress
          break
        case 'gesamt':
          valueA = a.progress.Gesamt.progress
          valueB = b.progress.Gesamt.progress
          break
        default:
          valueA = `${a.student.Vorname} ${a.student.Name}`.toLowerCase()
          valueB = `${b.student.Vorname} ${b.student.Name}`.toLowerCase()
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOrder === 'asc' 
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      } else {
        return sortOrder === 'asc' 
          ? (valueA as number) - (valueB as number)
          : (valueB as number) - (valueA as number)
      }
    })
  }

  // PDF-Export-Funktion
  const handleExportPDF = async () => {
    if (!selectedClass || studentProgress.length === 0) {
      alert('Bitte wählen Sie zuerst eine Klasse aus.')
      return
    }

    try {
      // Sortierte Schüler-Daten
      const sortedProgress = sortStudentProgress(studentProgress)
      
      // Erstelle HTML-Content für PDF
      const sortLabel = sortBy === 'name' ? 'Name' : 
                       sortBy === 'deutsch' ? 'Deutsch' :
                       sortBy === 'mathematik' ? 'Mathematik' :
                       sortBy === 'englisch' ? 'Englisch' : 'Gesamt'
      
      // Aktuelles Datum
      const currentDate = new Date().toLocaleDateString('de-DE', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
      
      // Zeitfenster-Information
      let dateRangeText = ''
      let timeWindowInfo = ''
      if (useDateRange && startDate && endDate) {
        dateRangeText = `<p style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">
          Zeitfenster: ${new Date(startDate).toLocaleDateString('de-DE')} bis ${new Date(endDate).toLocaleDateString('de-DE')}
        </p>`
        timeWindowInfo = `<p style="font-size: 13px; color: #059669; font-weight: 500; margin-top: 4px;">
          ⚠️ Historische Statistik (Zeitfenster ausgewählt)
        </p>`
      } else {
        timeWindowInfo = `<p style="font-size: 13px; color: #2563eb; font-weight: 500; margin-top: 4px;">
          ✓ Aktuelle Statistik (alle aktiven Lernpläne)
        </p>`
      }
      
      // Erstelle Schüler-Karten HTML
      let cardsHTML = ''
      sortedProgress.forEach((sp) => {
        // Setze fach für jeden Progress
        const deutschProgress = { ...sp.progress.Deutsch, fach: 'Deutsch' }
        const matheProgress = { ...sp.progress.Mathematik, fach: 'Mathematik' }
        const englischProgress = { ...sp.progress.Englisch, fach: 'Englisch' }
        const gesamtProgress = { ...sp.progress.Gesamt, fach: 'Gesamt' }
        
        const deutschHTML = createProgressHTML('Deutsch', deutschProgress, '#eab308')
        const matheHTML = createProgressHTML('Mathematik', matheProgress, '#3b82f6')
        const englischHTML = createProgressHTML('Englisch', englischProgress, '#ef4444')
        const gesamtHTML = createProgressHTML('Gesamt', gesamtProgress, '#10b981')
        
        // Schülerfoto
        let photoHTML = ''
        if (hasPhoto(studentPhotos[sp.student.ID])) {
          const photoDataUrl = createPhotoDataUrl(studentPhotos[sp.student.ID]!)
          photoHTML = `
            <img 
              src="${photoDataUrl}" 
              alt="${sp.student.Vorname} ${sp.student.Name}"
              style="width: 48px; height: 48px; border-radius: 50%; object-fit: cover; margin-right: 12px; flex-shrink: 0;"
            />
          `
        } else {
          // Platzhalter-Icon wenn kein Foto vorhanden
          photoHTML = `
            <div style="width: 48px; height: 48px; border-radius: 50%; background-color: #e5e7eb; display: flex; align-items: center; justify-content: center; margin-right: 12px; flex-shrink: 0;">
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #9ca3af;">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          `
        }
        
        cardsHTML += `
          <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; background-color: #ffffff; page-break-inside: avoid; margin-bottom: 10px;">
            <div style="margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center;">
              ${photoHTML}
              <div>
                <h3 style="font-size: 16px; font-weight: bold; color: #111827; margin-bottom: 4px; margin: 0;">
                  ${sp.student.Vorname} ${sp.student.Name}
                </h3>
                <p style="font-size: 12px; color: #6b7280; margin: 0;">Klasse ${sp.student.Klasse}</p>
              </div>
            </div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
              ${deutschHTML}
              ${matheHTML}
              ${englischHTML}
              ${gesamtHTML}
            </div>
          </div>
        `
      })
      
      // Erstelle vollständiges HTML-Dokument
      const htmlContent = `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Statistik - ${selectedClass}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            line-height: 1.6;
            color: #333;
            background: white;
            padding: 20mm;
            margin: 0;
        }
        
        .header {
            margin-bottom: 20px;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 15px;
        }
        
        .header h1 {
            font-size: 24px;
            font-weight: bold;
            color: #111827;
            margin-bottom: 8px;
        }
        
        .header p {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 4px;
        }
        
        .cards-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-top: 20px;
        }
        
        .circular-progress {
            page-break-inside: avoid;
        }
        
        @media print {
            @page {
                margin: 2cm;
                size: A4 portrait;
            }
            
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            
            body {
                background: white !important;
                padding: 0 !important;
            }
            
            .cards-container {
                grid-template-columns: repeat(2, 1fr) !important;
            }
            
            svg {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            
            svg circle {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            
            img {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
                max-width: 100%;
                height: auto;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Statistik - Lernplan-Fortschritt</h1>
        <p style="font-size: 16px; color: #6b7280; margin-bottom: 4px;">Klasse: ${selectedClass}</p>
        <p style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">
          <strong>Erstellt am:</strong> ${currentDate}
        </p>
        ${dateRangeText}
        ${timeWindowInfo}
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">
          Sortiert nach: ${sortLabel} (${sortOrder === 'asc' ? 'aufsteigend' : 'absteigend'})
        </p>
    </div>
    <div class="cards-container">
        ${cardsHTML}
    </div>
    
    <script>
        window.addEventListener('load', function() {
            console.log('📄 Statistik-PDF geladen');
        });
    </script>
</body>
</html>`

      // Öffne neues Fenster mit dem HTML-Content
      const printWindow = window.open('', '_blank', 'width=800,height=600,menubar=yes,toolbar=yes,scrollbars=yes,resizable=yes')
      
      if (!printWindow) {
        throw new Error('Popup-Blocker verhindert das Öffnen des Druckfensters')
      }

      printWindow.document.open()
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      
      // Fokussiere das neue Fenster
      printWindow.focus()
      
      // Öffne Druckdialog nach kurzer Verzögerung
      setTimeout(() => {
        if (printWindow.print && typeof printWindow.print === 'function') {
          printWindow.print()
        }
      }, 500)
      
    } catch (error) {
      console.error('Fehler beim Exportieren des PDFs:', error)
      alert(`Fehler beim Exportieren des PDFs: ${getErrorMessage(error)}`)
    }
  }
  
  // Hilfsfunktion zum Erstellen von SVG-Rundinstrumenten für PDF
  const createCircularProgressSVG = (progress: LearningPlanProgress, colorHex: string, size: number = 70): string => {
    const radius = (size - 8) / 2
    const circumference = 2 * Math.PI * radius
    const center = size / 2
    
    // Basis-Fortschritt (max 100%)
    const baseProgressClamped = Math.min(progress.baseProgress, 100)
    const baseStrokeDashoffset = circumference - (baseProgressClamped / 100) * circumference
    
    // Bonus-Fortschritt (zusätzlich über 100%)
    const bonusProgressClamped = Math.min(progress.bonusProgress, 100)
    const bonusStrokeDashoffset = circumference - (bonusProgressClamped / 100) * circumference
    
    // Bonus-Ring (wenn vorhanden)
    const bonusRing = progress.bonusProgress > 0 ? `
      <circle
        cx="${center}"
        cy="${center}"
        r="${radius - 6}"
        stroke="#fbbf24"
        stroke-width="2"
        fill="none"
        stroke-dasharray="${circumference * 0.85}"
        stroke-dashoffset="${bonusStrokeDashoffset * 0.85}"
        stroke-linecap="round"
        opacity="0.8"
        transform="rotate(-90 ${center} ${center})"
      />
    ` : ''
    
    return `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
        <div style="position: relative; width: ${size}px; height: ${size}px; margin-bottom: 4px;">
          <svg width="${size}" height="${size}" style="transform: rotate(-90deg);">
            <!-- Hintergrundkreis -->
            <circle
              cx="${center}"
              cy="${center}"
              r="${radius}"
              stroke="#e5e7eb"
              stroke-width="4"
              fill="none"
            />
            <!-- Basis-Fortschrittskreis -->
            <circle
              cx="${center}"
              cy="${center}"
              r="${radius}"
              stroke="${colorHex}"
              stroke-width="4"
              fill="none"
              stroke-dasharray="${circumference}"
              stroke-dashoffset="${baseStrokeDashoffset}"
              stroke-linecap="round"
            />
            ${bonusRing}
          </svg>
          <!-- Prozentanzeige -->
          <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; pointer-events: none;">
            <span style="font-size: ${size > 70 ? '14px' : '12px'}; font-weight: 600; color: #374151; line-height: 1.2;">
              ${progress.progress}%
            </span>
            ${progress.bonusProgress > 0 && size > 70 ? `
              <span style="font-size: 11px; color: #d97706; font-weight: 500; line-height: 1.2;">
                +${progress.bonusProgress}% ⭐
              </span>
            ` : ''}
          </div>
        </div>
        <span style="font-size: 12px; color: #4b5563; margin-top: 2px; font-weight: 500;">${progress.fach}</span>
        <span style="font-size: 11px; color: #6b7280; margin-top: 2px;">
          ${progress.completedRegularTasks}/${progress.regularTasks}
          ${progress.bonusTasks > 0 ? `<span style="color: #d97706;"> +${progress.completedBonusTasks}/${progress.bonusTasks}⭐</span>` : ''}
        </span>
        <span style="font-size: 10px; color: #9ca3af; margin-top: 2px;">
          ${progress.learningPlansCount} LP
        </span>
      </div>
    `
  }
  
  // Hilfsfunktion zum Erstellen von Fortschritts-HTML mit Rundinstrumenten
  const createProgressHTML = (label: string, progress: LearningPlanProgress, colorHex: string): string => {
    return `
      <div style="display: flex; justify-content: center; align-items: center; padding: 8px;">
        ${createCircularProgressSVG(progress, colorHex, 70)}
      </div>
    `
  }

  // Lade alle verfügbaren Klassen
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await apiClient.get('/students/classes')
        if (response.data.success) {
          setClasses(response.data.data)
        }
      } catch (error) {
        console.error('Fehler beim Laden der Klassen:', error)
      }
    }

    fetchClasses()
  }, [])

  useEffect(() => {
    fetchAssessmentConfigs()
  }, [canUseStudentAssessments])


  // Lade Lernplan-Fortschritt für alle Schüler
  useEffect(() => {
    if (selectedClass) {
      const fetchStudentProgress = async () => {
        setLoading(true)
        try {
          const response = await apiClient.get(buildProgressQueryUrl())
          if (response.data.success) {
            const studentProgressData = response.data.data
            setStudentProgress(studentProgressData)
            const students = studentProgressData.map((sp: any) => sp.student)
            await loadAllStudentPhotos(students)
          }
        } catch (error) {
          console.error('Fehler beim Laden des Lernplan-Fortschritts:', error)
          setStudentProgress([])
        } finally {
          setLoading(false)
        }
      }

      fetchStudentProgress()
    } else {
      setStudentProgress([])
    }
  }, [selectedClass, useDateRange, startDate, endDate])

  // Leere Auswahl, wenn sich die Klasse ändert
  useEffect(() => {
    setSelectedStudents(new Set())
  }, [selectedClass])

  useEffect(() => {
    setSubjectTasksCache({})
    clearHoverPreviewTimer()
    setSubjectTasksModal(null)
    setSubjectTasksError(null)
    setSubjectTasksLoadingKey(null)
    setSubjectTaskToggleBusyAid(null)
  }, [selectedClass, useDateRange, startDate, endDate])

  useEffect(() => {
    return () => {
      if (hoverPreviewTimerRef.current) {
        clearTimeout(hoverPreviewTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedClass) {
      applyAssessmentConfigToDraft(null)
      return
    }
    applyAssessmentConfigToDraft(assessmentConfigs[selectedClass] || null)
  }, [selectedClass, assessmentConfigs])

  useEffect(() => {
    if (!selectedClass || activeView !== 'trend' || !resolvedTrendRange.isValid) {
      return
    }

    let isCancelled = false

    const fetchEvidenceTrend = async () => {
      setEvidenceTrendLoading(true)
      setEvidenceTrendError(null)
      try {
        const params = new URLSearchParams()
        params.set('subject', evidenceTrendSubject)
        params.set('startDate', resolvedTrendRange.start)
        params.set('endDate', resolvedTrendRange.end)
        params.set('timeScope', useDateRange ? 'custom' : 'current')
        if (selectedChartStudentIds.length > 0) {
          params.set('studentIds', selectedChartStudentIds.join(','))
        }
        if (!includeClassAverage) {
          params.set('includeClassAverage', 'false')
        }

        const response = await apiClient.get(
          `/statistics/class/${selectedClass}/evidence-trend?${params.toString()}`
        )
        const payload = response.data as { success: boolean; data: EvidenceTrendData; error?: string }
        if (!payload.success) {
          throw new Error(payload.error || 'Unbekannter Fehler beim Laden des Linien-Diagramms')
        }
        if (!isCancelled) {
          setEvidenceTrendData(payload.data)
        }
      } catch (error) {
        if (!isCancelled) {
          setEvidenceTrendError(getErrorMessage(error))
          setEvidenceTrendData(null)
        }
      } finally {
        if (!isCancelled) {
          setEvidenceTrendLoading(false)
        }
      }
    }

    fetchEvidenceTrend()

    return () => {
      isCancelled = true
    }
  }, [
    selectedClass,
    activeView,
    resolvedTrendRange.start,
    resolvedTrendRange.end,
    resolvedTrendRange.isValid,
    evidenceTrendSubject,
    selectedChartStudentIds,
    selectedChartStudentIdsKey,
    includeClassAverage,
    useDateRange
  ])

  useEffect(() => {
    if (!selectedClass || activeView !== 'trend' || evidencePanelMode !== 'today') {
      return
    }

    let isCancelled = false

    const fetchEvidenceToday = async () => {
      setEvidenceTodayLoading(true)
      setEvidenceTodayError(null)
      try {
        const response = await apiClient.get(`/statistics/class/${selectedClass}/evidence-today`)
        const payload = response.data as { success: boolean; data: EvidenceTodayData; error?: string }
        if (!payload.success) {
          throw new Error(payload.error || 'Unbekannter Fehler beim Laden der Heute-Ansicht')
        }
        if (!isCancelled) {
          setEvidenceTodayData(payload.data)
        }
      } catch (error) {
        if (!isCancelled) {
          setEvidenceTodayError(getErrorMessage(error))
          setEvidenceTodayData(null)
        }
      } finally {
        if (!isCancelled) {
          setEvidenceTodayLoading(false)
        }
      }
    }

    fetchEvidenceToday()

    return () => {
      isCancelled = true
    }
  }, [selectedClass, activeView, evidencePanelMode])

  useEffect(() => {
    setEvidenceTrendData(null)
    setEvidenceTrendError(null)
    setEvidenceTodayData(null)
    setEvidenceTodayError(null)
    setHeatmapData(null)
    setHeatmapError(null)
  }, [selectedClass])

  useEffect(() => {
    if (!selectedClass || activeView !== 'heatmap') return

    let isCancelled = false
    const fetchHeatmap = async () => {
      setHeatmapLoading(true)
      setHeatmapError(null)
      try {
        const params = new URLSearchParams()
        if (heatmapSubject !== 'ALL') params.set('subject', heatmapSubject)
        const response = await apiClient.get(
          `/statistics/class/${selectedClass}/heatmap?${params.toString()}`
        )
        if (!response.data.success) {
          throw new Error(response.data.error || 'Fehler beim Laden der Heatmap')
        }
        if (!isCancelled) setHeatmapData(response.data.data)
      } catch (error) {
        if (!isCancelled) {
          setHeatmapError(getErrorMessage(error))
          setHeatmapData(null)
        }
      } finally {
        if (!isCancelled) setHeatmapLoading(false)
      }
    }

    fetchHeatmap()
    return () => { isCancelled = true }
  }, [selectedClass, activeView, heatmapSubject])



  // Rundinstrument-Komponente mit Bonus-Anzeige
  const CircularProgress: React.FC<{
    progress: LearningPlanProgress
    color: string
    size?: number
  }> = ({ progress, color, size = 80 }) => {
    const radius = (size - 8) / 2
    const circumference = 2 * Math.PI * radius
    const strokeDasharray = circumference
    
    // Basis-Fortschritt (max 100%)
    const baseProgressClamped = Math.min(progress.baseProgress, 100)
    const baseStrokeDashoffset = circumference - (baseProgressClamped / 100) * circumference
    
    // Bonus-Fortschritt (zusätzlich über 100%)
    const bonusProgressClamped = Math.min(progress.bonusProgress, 100) // Max 100% Bonus
    const bonusStrokeDashoffset = circumference - (bonusProgressClamped / 100) * circumference

    return (
      <div className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <svg
            width={size}
            height={size}
            className="transform -rotate-90"
          >
            {/* Hintergrundkreis */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              className="text-gray-200 dark:text-gray-700"
            />
            {/* Basis-Fortschrittskreis */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={baseStrokeDashoffset}
              strokeLinecap="round"
              className={`transition-all duration-300 ${color}`}
            />
            {/* Bonus-Fortschrittskreis (äußerer Ring, falls Bonus vorhanden) */}
            {progress.bonusProgress > 0 && (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius - 6}
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeDasharray={circumference * 0.85} // Etwas kleiner für äußeren Ring
                strokeDashoffset={bonusStrokeDashoffset * 0.85}
                strokeLinecap="round"
                className="text-yellow-400 opacity-80"
              />
            )}
          </svg>
          {/* Prozentanzeige */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`${size > 70 ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 dark:text-gray-300`}>
              {progress.progress}%
            </span>
            {progress.bonusProgress > 0 && size > 70 && (
              <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                +{progress.bonusProgress}% ⭐
              </span>
            )}
          </div>
        </div>
        <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">{progress.fach}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {progress.completedRegularTasks}/{progress.regularTasks}
          {progress.bonusTasks > 0 && (
            <span className="text-yellow-600 dark:text-yellow-400"> +{progress.completedBonusTasks}/{progress.bonusTasks}⭐</span>
          )}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {progress.learningPlansCount} LP
        </span>
      </div>
    )
  }

  // Schüler-Kachel-Komponente
  const StudentCard: React.FC<{ studentProgress: StudentProgress }> = ({ studentProgress }) => {
    const { student, progress } = studentProgress
    const isSelected = selectedStudents.has(student.ID)

    const handleInfoClick = () => {
      setSelectedStudentInfo(studentProgress)
      setShowStudentModal(true)
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleStudentSelection(student.ID, e.target.checked)
    }

    const renderInteractiveGauge = (
      subject: ProgressSubject,
      subjectProgress: LearningPlanProgress,
      color: string
    ) => (
      <div
        className="cursor-pointer rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-40"
        onMouseEnter={(event) => handleGaugeMouseEnter(student, subject, event.currentTarget)}
        onMouseMove={() => handleGaugeMouseMove(student.ID, subject)}
        onMouseLeave={() => handleGaugeMouseLeave(student.ID, subject)}
        onClick={() => handleGaugeClick(student, subject)}
        role="button"
        tabIndex={0}
        title={`${subject}: Aufgaben anzeigen`}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleGaugeClick(student, subject)
          }
        }}
      >
        <CircularProgress
          progress={subjectProgress}
          color={color}
          size={70}
        />
      </div>
    )

    return (
      <div className={`card p-4 hover:shadow-lg transition-shadow relative ${isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}>
        {/* Checkbox */}
        <div className="absolute top-2 left-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
            title="Schüler für E-Mail-Versand auswählen"
          />
        </div>
        
        {/* Info-Button */}
        <button
          onClick={handleInfoClick}
          className="absolute top-2 right-2 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors text-xs font-bold"
          title="Detaillierte Informationen anzeigen"
        >
          i
        </button>
        <div className="flex items-center mb-4">
          {hasPhoto(studentPhotos[student.ID]) ? (
            <img
              src={createPhotoDataUrl(studentPhotos[student.ID]!)}
              alt={`${student.Vorname} ${student.Name}`}
              className="w-12 h-12 rounded-full object-cover mr-3"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-3">
              <Users className="w-6 h-6 text-gray-400 dark:text-gray-500" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {student.Vorname} {student.Name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Klasse {student.Klasse}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {renderInteractiveGauge('Deutsch', progress.Deutsch, 'text-yellow-500')}
          {renderInteractiveGauge('Mathematik', progress.Mathematik, 'text-blue-500')}
          {renderInteractiveGauge('Englisch', progress.Englisch, 'text-red-500')}
          {renderInteractiveGauge('Gesamt', progress.Gesamt, 'text-green-500')}
        </div>
      </div>
    )
  }

  const EvidenceTooltipContent: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null
    }

    const labelDate = label
      ? new Date(label).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      })
      : ''

    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 max-w-xs sm:max-w-md">
        <p className="text-xs uppercase text-gray-500 dark:text-gray-400">{labelDate}</p>
        <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
          {payload.map((entry) => (
            <div
              key={entry.dataKey as string}
              className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300 min-w-0"
            >
              <span className="flex items-center min-w-0">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full mr-2 border border-white shadow"
                  style={{ backgroundColor: entry.color || '#6b7280' }}
                />
                <span className="truncate">{entry.name}</span>
              </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100 ml-2">
                {typeof entry.value === 'number'
                  ? evidenceNumberFormatter.format(entry.value)
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const activeSubjectTasksCacheKey = subjectTasksModal
    ? getSubjectTasksCacheKey(subjectTasksModal.student.ID, subjectTasksModal.subject)
    : null
  const activeSubjectTasksData = activeSubjectTasksCacheKey
    ? subjectTasksCache[activeSubjectTasksCacheKey]
    : null
  const isActiveSubjectTasksLoading = Boolean(
    activeSubjectTasksCacheKey && subjectTasksLoadingKey === activeSubjectTasksCacheKey
  )
  // Gleiche Reihenfolge wie Lernbüro Panel B (GET /lernbuero/tasks): serverseitig LPID absteigend,
  // innerhalb LP nach Sortierung — ohne Erledigt zuerst oder Fach-Locale.
  const activeSubjectTasksInPanelOrder = (activeSubjectTasksData?.tasks || []).slice()
  const learningPlanColorPalette = [
    '#000000',
    '#0000FF',
    '#00AA00',
    '#FF0000',
    '#D97706',
    '#9333EA',
    '#0891B2',
    '#16A34A',
    '#92400E',
    '#475569'
  ]
  const activeLearningPlans = Array.from(
    new Map(
      activeSubjectTasksInPanelOrder.map((task) => [task.lpid, { lpid: task.lpid, lernplanName: task.lernplanName }])
    ).values()
  )
  const activeLearningPlanColorMap = activeLearningPlans.reduce<Record<number, string>>((map, plan, index) => {
    map[plan.lpid] = learningPlanColorPalette[index % learningPlanColorPalette.length]
    return map
  }, {})

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <BarChart3 className="w-8 h-8 mr-3 text-primary-600 dark:text-primary-400" />
            Statistik
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Lernplan-Fortschritt nach Klassen und Schülern
          </p>
        </div>
      </div>

      {/* ═══ STEUERLEISTE: Klassenauswahl + Zeitraum + E-Mail ═══ */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
          Klasse &amp; Zeitraum</h2>
        
        <div className="flex items-center space-x-4 mb-4">
          <label htmlFor="class-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Klasse auswählen:
          </label>
          <select
            id="class-select"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">-- Klasse wählen --</option>
            {classes.map((cls) => (
              <option key={cls.Klasse} value={cls.Klasse}>
                {cls.Klasse}
              </option>
            ))}
          </select>
          
          {selectedClass && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              {studentProgress.length} Schüler in Klasse {selectedClass}
            </div>
          )}
        </div>

        {/* Sortierungsoptionen */}
        {selectedClass && studentProgress.length > 0 && (
          <div className="flex items-center space-x-4 mb-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <label htmlFor="sort-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Sortieren nach:
            </label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'deutsch' | 'mathematik' | 'englisch' | 'gesamt')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="name">Name (A-Z)</option>
              <option value="deutsch">Deutsch (%)</option>
              <option value="mathematik">Mathematik (%)</option>
              <option value="englisch">Englisch (%)</option>
              <option value="gesamt">Gesamt (%)</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="btn btn-sm btn-outline"
              title={`Sortierung ${sortOrder === 'asc' ? 'aufsteigend' : 'absteigend'} - Klicken zum Umschalten`}
            >
              {sortOrder === 'asc' ? (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  Aufsteigend
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Absteigend
                </>
              )}
            </button>
          </div>
        )}

        {/* Aktuelle Sortierung anzeigen */}
        {selectedClass && studentProgress.length > 0 && sortBy !== 'name' && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
            <div className="flex items-center text-sm text-blue-800 dark:text-blue-200">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              </svg>
              Sortiert nach: <strong>{sortBy === 'deutsch' ? 'Deutsch' : sortBy === 'mathematik' ? 'Mathematik' : sortBy === 'englisch' ? 'Englisch' : 'Gesamt'}</strong> 
              ({sortOrder === 'asc' ? 'aufsteigend' : 'absteigend'})
            </div>
          </div>
        )}

        {/* PDF-Export Button */}
        {selectedClass && studentProgress.length > 0 && (
          <div className="mb-4">
            <button
              onClick={handleExportPDF}
              className="btn btn-md bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400 shadow-sm"
              title="Aktuelle Ansicht als PDF speichern (berücksichtigt Zeitfenster und Sortierung)"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Aktuelle Ansicht als PDF speichern
            </button>
          </div>
        )}

        {/* Datumsauswahl */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center space-x-4 mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useDateRange}
                onChange={(e) => setUseDateRange(e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Zeitfenster auswählen (z.B. 6 Monate)
              </span>
            </label>
          </div>

          {useDateRange && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Von Datum:
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bis Datum:
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  min={minDate}
                  max={maxDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          )}

          {useDateRange && (
            <div className="mt-3">
              {/* Schnellauswahl für häufige Zeiträume */}
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Schnellauswahl:</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      const end = new Date()
                      const start = new Date()
                      start.setMonth(start.getMonth() - 1)
                      
                      // Stelle sicher, dass die Daten innerhalb des Schuljahrs liegen
                      const startDate = start < schoolYear.start ? schoolYear.start : start
                      const endDate = end > schoolYear.end ? schoolYear.end : end
                      
                      setStartDate(startDate.toISOString().split('T')[0])
                      setEndDate(endDate.toISOString().split('T')[0])
                    }}
                    className="btn btn-xs btn-secondary"
                  >
                    Letzter Monat
                  </button>
                  <button
                    onClick={() => {
                      const end = new Date()
                      const start = new Date()
                      start.setMonth(start.getMonth() - 3)
                      
                      // Stelle sicher, dass die Daten innerhalb des Schuljahrs liegen
                      const startDate = start < schoolYear.start ? schoolYear.start : start
                      const endDate = end > schoolYear.end ? schoolYear.end : end
                      
                      setStartDate(startDate.toISOString().split('T')[0])
                      setEndDate(endDate.toISOString().split('T')[0])
                    }}
                    className="btn btn-xs btn-secondary"
                  >
                    Letzte 3 Monate
                  </button>
                  <button
                    onClick={() => {
                      const end = new Date()
                      const start = new Date()
                      start.setMonth(start.getMonth() - 6)
                      
                      // Stelle sicher, dass die Daten innerhalb des Schuljahrs liegen
                      const startDate = start < schoolYear.start ? schoolYear.start : start
                      const endDate = end > schoolYear.end ? schoolYear.end : end
                      
                      setStartDate(startDate.toISOString().split('T')[0])
                      setEndDate(endDate.toISOString().split('T')[0])
                    }}
                    className="btn btn-xs btn-secondary"
                  >
                    Letzte 6 Monate
                  </button>
                  <button
                    onClick={() => {
                      const end = new Date()
                      const start = new Date()
                      start.setFullYear(start.getFullYear() - 1)
                      
                      // Stelle sicher, dass die Daten innerhalb des Schuljahrs liegen
                      const startDate = start < schoolYear.start ? schoolYear.start : start
                      const endDate = end > schoolYear.end ? schoolYear.end : end
                      
                      setStartDate(startDate.toISOString().split('T')[0])
                      setEndDate(endDate.toISOString().split('T')[0])
                    }}
                    className="btn btn-xs btn-secondary"
                  >
                    Letztes Jahr
                  </button>
                </div>
              </div>

              {startDate && endDate && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Zeitfenster:</strong> {new Date(startDate).toLocaleDateString('de-DE')} bis {new Date(endDate).toLocaleDateString('de-DE')}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Es werden alle Lernpläne angezeigt, die in diesem Zeitraum aktiv waren.
                    <br />
                    Belege werden für ALLE Aufgaben dieser Lernpläne berücksichtigt (unabhängig vom Beleg-Datum).
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* E-Mail-Versand an Eltern */}
      {selectedClass && studentProgress.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Statistik per E-Mail an Eltern senden
          </h2>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selectedStudents.size} von {studentProgress.length} Schülern ausgewählt
              </span>
              {selectedStudents.size > 0 && (
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-950/50 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                  {Math.round((selectedStudents.size / studentProgress.length) * 100)}% ausgewählt
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSelectAll}
              className="btn btn-md btn-secondary"
              disabled={selectedStudents.size === studentProgress.length}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Alle auswählen
            </button>
            
            {selectedStudents.size > 0 && (
              <button
                onClick={handleDeselectAll}
                className="btn btn-md btn-secondary"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Alle abwählen
              </button>
            )}
            
            <button
              onClick={handleSendRawStatistics}
              className="btn btn-md bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
              disabled={selectedStudents.size === 0}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Reine Statistik an Eltern schicken
            </button>
            
            <button
              onClick={() => {
                alert('Funktion wird bald freigeschaltet');
              }}
              className="btn btn-md btn-secondary opacity-60 cursor-not-allowed"
              disabled={true}
              title="Funktion wird bald freigeschaltet"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Interpretierte Statistik an Eltern schicken
            </button>
          </div>

          {selectedStudents.size > 0 && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Ausgewählte Schüler für E-Mail:</strong>{' '}
                {Array.from(selectedStudents).map(sid => {
                  const sp = studentProgress.find(s => s.student.ID === sid)
                  return sp ? `${sp.student.Vorname} ${sp.student.Name}` : ''
                }).filter(Boolean).join(', ')}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Diese Auswahl steuert auch die Schüler-Linien im Belege-Diagramm.
              </p>
            </div>
          )}
        </div>
      )}

      {canUseStudentAssessments && selectedClass && (
        <div className="card p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lernstand-Automatik</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manuelle Läufe starten und Basiskonfiguration für Klasse {selectedClass} pflegen.
              </p>
            </div>
            <Link
              to="/student-assessments"
              className="btn btn-sm btn-outline"
            >
              Detailseite öffnen
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Automatik aktiv</label>
              <button
                type="button"
                onClick={() => {
                  setAssessmentConfigDraft((prev) => ({ ...prev, enabled: !prev.enabled }))
                  setIsAssessmentConfigDirty(true)
                }}
                disabled={assessmentConfigSaving || assessmentConfigLoading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  assessmentConfigDraft.enabled ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 transition ${
                    assessmentConfigDraft.enabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min-Alarm</label>
              <select
                value={assessmentConfigDraft.minAlertLevel}
                onChange={(e) => {
                  setAssessmentConfigDraft((prev) => ({
                    ...prev,
                    minAlertLevel: e.target.value as StudentAssessmentAlertLevel
                  }))
                  setIsAssessmentConfigDirty(true)
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-900 dark:text-gray-100"
              >
                {assessmentAlertLevelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cooldown (Minuten)</label>
              <input
                type="number"
                min={15}
                step={15}
                value={assessmentConfigDraft.eventCooldownMinutes}
                onChange={(e) => {
                  const nextValue = Math.max(15, Number(e.target.value) || 15)
                  setAssessmentConfigDraft((prev) => ({ ...prev, eventCooldownMinutes: nextValue }))
                  setIsAssessmentConfigDirty(true)
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSaveAssessmentClassConfig}
              disabled={!selectedClass || !isAssessmentConfigDirty || assessmentConfigSaving}
              className="btn btn-md btn-primary"
            >
              {assessmentConfigSaving ? 'Speichere...' : 'Klassenkonfig speichern'}
            </button>
            <button
              onClick={handleRunAssessmentForSelectedStudents}
              disabled={assessmentRunBusy || selectedStudents.size === 0}
              className="btn btn-md bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
            >
              Auswertung für ausgewählte Schüler starten
            </button>
            <button
              onClick={handleRunAssessmentForClass}
              disabled={assessmentRunBusy || !selectedClass}
              className="btn btn-md bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400"
            >
              Auswertung für ganze Klasse starten
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            {assessmentConfigLoading
              ? 'Lade Lernstand-Konfiguration...'
              : isAssessmentConfigDirty
                ? 'Ungespeicherte Änderungen vorhanden.'
                : 'Konfiguration ist gespeichert.'}
          </p>
        </div>
      )}

      {/* ═══ ANSICHTS-TABS ═══ */}
      {selectedClass && (
        <div className="card">
          {/* Tab-Leiste */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 pt-4">
            <nav className="-mb-px flex gap-6">
              {([
                { key: 'gauges', label: 'Fortschritts-Übersicht', icon: <TrendingUp className="w-4 h-4" /> },
                { key: 'trend', label: 'Belege über die Zeit', icon: <LineChartIcon className="w-4 h-4" /> },
                { key: 'heatmap', label: 'Aktivitäts-Heatmap', icon: <CalendarDays className="w-4 h-4" /> },
              ] as const).map(({ key, label, icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveView(key)}
                  className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeView === key
                      ? 'border-primary-600 dark:border-primary-400 text-primary-700 dark:text-primary-300'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">

            {/* ── Tab: Fortschritts-Übersicht (Rundinstrumente) ── */}
            {activeView === 'gauges' && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
                    <span className="ml-2 text-gray-600 dark:text-gray-400">Lade Daten…</span>
                  </div>
                ) : studentProgress.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {sortStudentProgress(studentProgress).map((sp) => (
                      <StudentCard key={sp.student.ID} studentProgress={sp} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                    <p>Keine Schüler in dieser Klasse gefunden</p>
                  </div>
                )}
              </>
            )}

            {/* ── Tab: Belege über die Zeit (Liniendiagramm) ── */}
            {activeView === 'trend' && (
              <>
                {/* Trend/Heute-Umschalter */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 p-1 bg-gray-50 dark:bg-gray-800/50">
                    <button type="button" onClick={() => setEvidencePanelMode('trend')}
                      className={`px-3 py-1 text-sm rounded ${evidencePanelMode === 'trend' ? 'bg-white dark:bg-gray-800 text-primary-700 dark:text-primary-300 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}>
                      Verlauf
                    </button>
                    <button type="button" onClick={() => setEvidencePanelMode('today')}
                      className={`px-3 py-1 text-sm rounded ${evidencePanelMode === 'today' ? 'bg-white dark:bg-gray-800 text-primary-700 dark:text-primary-300 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}>
                      Heute
                    </button>
                  </div>
                </div>

                {evidencePanelMode === 'trend' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fachfilter</label>
                        <select value={evidenceTrendSubject}
                          onChange={(e) => setEvidenceTrendSubject(e.target.value as EvidenceTrendSubject)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-900 dark:text-gray-100">
                          <option value="ALL">Alle Fächer</option>
                          <option value="Deutsch">Deutsch</option>
                          <option value="Mathematik">Mathematik</option>
                          <option value="Englisch">Englisch</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Klassenmittelwert</label>
                        <div className="flex items-center space-x-3 mt-1">
                          <button type="button" onClick={() => setIncludeClassAverage(p => !p)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${includeClassAverage ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 transition ${includeClassAverage ? 'translate-x-5' : 'translate-x-1'}`} />
                          </button>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{includeClassAverage ? 'Aktiv' : 'Aus'}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">Zeitraum</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{trendRangeLabel}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {useDateRange ? 'basierend auf Zeitraumauswahl oben' : `Schuljahr ${schoolYear.label}`}
                        </p>
                      </div>
                    </div>

                    {!resolvedTrendRange.isValid && (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-800 dark:text-yellow-200 rounded-md mb-4">
                        Bitte wähle oben sowohl Start- als auch Enddatum aus, um den Verlauf anzuzeigen.
                      </div>
                    )}
                    {resolvedTrendRange.isValid && (
                      <>
                        {selectedChartStudentIds.length === 0 && (
                          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md text-sm text-blue-800 dark:text-blue-200">
                            Tipp: Wähle im E-Mail-Bereich einzelne Schüler aus, um deren Linien neben dem Klassenmittelwert einzublenden.
                          </div>
                        )}
                        {evidenceTrendError && (
                          <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
                            {evidenceTrendError}
                          </div>
                        )}
                        <div className="h-[30rem]">
                          {evidenceTrendLoading ? (
                            <div className="flex h-full items-center justify-center space-x-2 text-gray-500 dark:text-gray-400">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400" />
                              <span>Diagramm wird geladen…</span>
                            </div>
                          ) : evidenceTrendData && evidenceTrendData.series.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsLineChart data={evidenceTrendData.timeline} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} stroke={chartAxisColor} fontSize={12} />
                                <YAxis stroke={chartAxisColor} fontSize={12} allowDecimals tickFormatter={(v) => evidenceNumberFormatter.format(v as number)} />
                                <Tooltip content={<EvidenceTooltipContent />} />
                                <Legend />
                                {evidenceTrendData.series.map((series) => {
                                  const vs = evidenceSeriesStyles[series.key] || { color: '#2563eb', dash: undefined, strokeWidth: series.type === 'classAverage' ? 3 : 2 }
                                  return (
                                    <Line key={series.key} type="monotone" dataKey={series.key} name={series.label}
                                      stroke={vs.color} strokeWidth={vs.strokeWidth} strokeDasharray={vs.dash}
                                      dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                                  )
                                })}
                              </RechartsLineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 rounded-md">
                              Keine Belege im aktuellen Zeitraum gefunden.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {evidencePanelMode === 'today' && (
                  <>
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md text-sm text-blue-800 dark:text-blue-200">
                      Heute erworbene Leistungsbelege für alle Schüler der Klasse. Die Liste ist nach Anzahl (absteigend) sortiert.
                    </div>
                    {evidenceTodayError && <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">{evidenceTodayError}</div>}
                    {evidenceTodayLoading ? (
                      <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400 py-12">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400" />
                        <span>Heute-Daten werden geladen…</span>
                      </div>
                    ) : evidenceTodayData ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                          {[
                            { label: 'Datum', value: new Date(evidenceTodayData.date).toLocaleDateString('de-DE') },
                            { label: 'Leistungsbelege heute', value: evidenceTodayData.totalBelege, bold: true },
                            { label: 'Schüler mit Belegen', value: `${evidenceTodayData.studentsWithBelege}/${evidenceTodayData.totalStudents}` },
                            { label: 'Ohne Belege', value: Math.max(0, evidenceTodayData.totalStudents - evidenceTodayData.studentsWithBelege) },
                          ].map(({ label, value, bold }) => (
                            <div key={label} className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                              <p className="text-xs uppercase text-gray-500 dark:text-gray-400">{label}</p>
                              <p className={`${bold ? 'text-xl font-bold text-primary-700 dark:text-primary-300' : 'text-sm font-semibold text-gray-900 dark:text-gray-100'}`}>{value}</p>
                            </div>
                          ))}
                        </div>
                        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-800/50">
                              <tr>
                                {['Schüler', 'Gesamt', 'Deutsch', 'Mathematik', 'Englisch'].map((h, i) => (
                                  <th key={h} className={`px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                              {evidenceTodayData.students.map((s) => (
                                <tr key={s.studentId} className={s.totalBelege > 0 ? 'bg-green-50 dark:bg-green-950/30/40' : ''}>
                                  <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{s.label}</td>
                                  <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">{s.totalBelege}</td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{s.deutschBelege}</td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{s.mathematikBelege}</td>
                                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{s.englischBelege}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-600 dark:text-gray-400">
                        Keine Heute-Daten verfügbar.
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* ── Tab: Aktivitäts-Heatmap ── */}
            {activeView === 'heatmap' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fachfilter</label>
                  <select value={heatmapSubject}
                    onChange={(e) => setHeatmapSubject(e.target.value as typeof heatmapSubject)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                    <option value="ALL">Alle Fächer</option>
                    <option value="Deutsch">Deutsch</option>
                    <option value="Mathematik">Mathematik</option>
                    <option value="Englisch">Englisch</option>
                  </select>
                </div>
                {heatmapError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">{heatmapError}</div>
                )}
                <ActivityHeatmap
                  rangeStart={heatmapData?.rangeStart ?? ''}
                  rangeEnd={heatmapData?.rangeEnd ?? ''}
                  subject={heatmapData?.subject ?? heatmapSubject}
                  students={heatmapData?.students ?? []}
                  classDailyTotals={heatmapData?.classDailyTotals ?? {}}
                  classAverageDailyTotals={heatmapData?.classAverageDailyTotals ?? {}}
                  classTopTeachers={heatmapData?.classTopTeachers ?? []}
                  loading={heatmapLoading}
                />
              </>
            )}

          </div>
        </div>
      )}

      {/* Hover-Vorschau für Rundinstrumente */}
      {subjectTasksModal?.mode === 'hover' && (
        <div
          className="fixed z-40 pointer-events-none"
          style={{
            top: Math.min(subjectTasksModal.anchorTop || 0, window.innerHeight - 320),
            left: Math.min(Math.max((subjectTasksModal.anchorLeft || 0) - 200, 12), window.innerWidth - 412)
          }}
        >
          <div className="w-[400px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3">
            <div className="mb-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {subjectTasksModal.student.Vorname} {subjectTasksModal.student.Name} - {subjectTasksModal.subject}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Bearbeitet: {activeSubjectTasksData?.completedCount ?? 0}/{activeSubjectTasksData?.totalCount ?? 0}
              </p>
            </div>
            {isActiveSubjectTasksLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Lade Aufgaben...</p>
            ) : subjectTasksError ? (
              <p className="text-sm text-red-600 dark:text-red-400">{subjectTasksError}</p>
            ) : activeSubjectTasksInPanelOrder.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Keine Aufgaben in diesem Zeitraum.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                  {activeSubjectTasksInPanelOrder.map((task) => {
                    const tileColor = activeLearningPlanColorMap[task.lpid] || '#808080'
                    return (
                      <div
                        key={task.aid}
                        className={`relative h-16 rounded-lg border-2 px-1 py-1 ${
                          task.completed
                            ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                            : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800'
                        }`}
                        style={{ backgroundColor: task.completed ? `${tileColor}33` : '#e9ecef' }}
                      >
                        <div
                          className="absolute top-1 left-1 w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: tileColor }}
                        />
                        <div className="h-full w-full flex flex-col items-center justify-center px-1 text-center">
                          <p className={`text-[10px] leading-tight font-medium ${task.completed ? 'text-green-700 dark:text-green-300 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
                            {task.kurzbezeichnung}
                          </p>
                          {task.zusatzaufgabe && <span className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">⭐</span>}
                        </div>
                        <div className="absolute right-1 bottom-1 text-[10px]">
                          {task.completed ? '✓' : '□'}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {activeLearningPlans.length > 0 && (
                  <div className="flex flex-wrap gap-2 border-t border-gray-100 dark:border-gray-800 pt-2">
                    {activeLearningPlans.slice(0, 6).map((plan) => (
                      <div key={plan.lpid} className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: activeLearningPlanColorMap[plan.lpid] || '#808080' }}
                        />
                        <span className="truncate max-w-[95px]">{plan.lernplanName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Klick-Modal für Rundinstrumente */}
      {subjectTasksModal?.mode === 'click' && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="card shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {subjectTasksModal.student.Vorname} {subjectTasksModal.student.Name} - {subjectTasksModal.subject}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bearbeitet: {activeSubjectTasksData?.completedCount ?? 0}/{activeSubjectTasksData?.totalCount ?? 0}
                </p>
              </div>
              <button
                type="button"
                onClick={closeSubjectTasksModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-400 transition-colors text-2xl leading-none"
                aria-label="Modal schließen"
              >
                ×
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(85vh-80px)]">
              {isActiveSubjectTasksLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Lade Aufgaben...</p>
              ) : subjectTasksError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{subjectTasksError}</p>
              ) : activeSubjectTasksInPanelOrder.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Keine Aufgaben in diesem Zeitraum.</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {activeSubjectTasksInPanelOrder.map((task) => {
                      const tileColor = activeLearningPlanColorMap[task.lpid] || '#808080'
                      const isBusy = subjectTaskToggleBusyAid === task.aid
                      return (
                        <button
                          key={task.aid}
                          type="button"
                          onClick={() => handleSubjectTaskToggle(task)}
                          disabled={isBusy}
                          className={`relative h-24 w-full rounded-lg border-2 p-2 text-center transition-all duration-200 ${
                            task.completed
                              ? 'border-green-500 bg-green-50 dark:bg-green-950/30 shadow-sm'
                              : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-sm'
                          } ${isBusy ? 'opacity-60 cursor-wait' : ''}`}
                          style={{ backgroundColor: task.completed ? `${tileColor}40` : '#e9ecef' }}
                          title={task.completed ? 'Als nicht erledigt markieren' : 'Als erledigt markieren'}
                        >
                          <div
                            className="absolute top-1 left-1 w-3.5 h-3.5 rounded-full"
                            style={{ backgroundColor: tileColor }}
                          />
                          <div className="h-full flex flex-col items-center justify-center px-1">
                            <p className={`text-xs leading-tight font-medium ${task.completed ? 'text-green-700 dark:text-green-300 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
                              {task.kurzbezeichnung}
                            </p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 truncate max-w-full">{task.lernplanName}</p>
                            {task.zusatzaufgabe && <span className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">⭐ Zusatz</span>}
                          </div>
                          <div className="absolute right-1.5 bottom-1 text-sm text-green-600 dark:text-green-400">
                            {task.completed ? '✓' : '□'}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  {activeLearningPlans.length > 0 && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Lernpläne</h4>
                      <div className="flex flex-wrap gap-2">
                        {activeLearningPlans.map((plan) => (
                          <div key={plan.lpid} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: activeLearningPlanColorMap[plan.lpid] || '#808080' }}
                            />
                            <span className="truncate max-w-[170px]">{plan.lernplanName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schüler-Detail-Modal */}
      {showStudentModal && selectedStudentInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="card shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                {hasPhoto(studentPhotos[selectedStudentInfo.student.ID]) ? (
                  <img
                    src={createPhotoDataUrl(studentPhotos[selectedStudentInfo.student.ID]!)}
                    alt={`${selectedStudentInfo.student.Vorname} ${selectedStudentInfo.student.Name}`}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-4">
                    <Users className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {selectedStudentInfo.student.Vorname} {selectedStudentInfo.student.Name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">Klasse {selectedStudentInfo.student.Klasse}</p>
                </div>
              </div>
              <button
                onClick={() => setShowStudentModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-400 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Übersicht der Rundinstrumente */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Fortschrittsübersicht</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <CircularProgress
                      progress={selectedStudentInfo.progress.Deutsch}
                      color="text-yellow-500"
                      size={100}
                    />
                  </div>
                  <div className="text-center">
                    <CircularProgress
                      progress={selectedStudentInfo.progress.Mathematik}
                      color="text-blue-500"
                      size={100}
                    />
                  </div>
                  <div className="text-center">
                    <CircularProgress
                      progress={selectedStudentInfo.progress.Englisch}
                      color="text-red-500"
                      size={100}
                    />
                  </div>
                  <div className="text-center">
                    <CircularProgress
                      progress={selectedStudentInfo.progress.Gesamt}
                      color="text-green-500"
                      size={100}
                    />
                  </div>
                </div>
              </div>

              {/* Detaillierte Statistiken */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Detaillierte Statistiken</h3>
                
                {/* Deutsch */}
                <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
                    Deutsch
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Lernpläne:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Deutsch.learningPlansCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Reguläre Aufgaben:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Deutsch.completedRegularTasks}/{selectedStudentInfo.progress.Deutsch.regularTasks}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Basis-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-yellow-600 dark:text-yellow-400">{selectedStudentInfo.progress.Deutsch.baseProgress}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Gesamt-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-yellow-800 dark:text-yellow-200">{selectedStudentInfo.progress.Deutsch.progress}%</span>
                    </div>
                  </div>
                  {selectedStudentInfo.progress.Deutsch.bonusTasks > 0 && (
                    <div className="mt-3 p-3 bg-yellow-100 dark:bg-yellow-950/50 rounded-lg border border-yellow-300 dark:border-yellow-700">
                      <h5 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2 flex items-center">
                        ⭐ Zusatzaufgaben (Bonus)
                      </h5>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Zusatzaufgaben:</span>
                          <span className="font-semibold ml-2">{selectedStudentInfo.progress.Deutsch.completedBonusTasks}/{selectedStudentInfo.progress.Deutsch.bonusTasks}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Bonus-Fortschritt:</span>
                          <span className="font-semibold ml-2 text-yellow-600 dark:text-yellow-400">+{selectedStudentInfo.progress.Deutsch.bonusProgress}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mathematik */}
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                    Mathematik
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Lernpläne:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Mathematik.learningPlansCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Reguläre Aufgaben:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Mathematik.completedRegularTasks}/{selectedStudentInfo.progress.Mathematik.regularTasks}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Basis-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-blue-600 dark:text-blue-400">{selectedStudentInfo.progress.Mathematik.baseProgress}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Gesamt-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-blue-800 dark:text-blue-200">{selectedStudentInfo.progress.Mathematik.progress}%</span>
                    </div>
                  </div>
                  {selectedStudentInfo.progress.Mathematik.bonusTasks > 0 && (
                    <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-950/50 rounded-lg border border-blue-300 dark:border-blue-700">
                      <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                        ⭐ Zusatzaufgaben (Bonus)
                      </h5>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Zusatzaufgaben:</span>
                          <span className="font-semibold ml-2">{selectedStudentInfo.progress.Mathematik.completedBonusTasks}/{selectedStudentInfo.progress.Mathematik.bonusTasks}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Bonus-Fortschritt:</span>
                          <span className="font-semibold ml-2 text-blue-600 dark:text-blue-400">+{selectedStudentInfo.progress.Mathematik.bonusProgress}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Englisch */}
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 dark:text-red-200 mb-3 flex items-center">
                    <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
                    Englisch
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Lernpläne:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Englisch.learningPlansCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Reguläre Aufgaben:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Englisch.completedRegularTasks}/{selectedStudentInfo.progress.Englisch.regularTasks}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Basis-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-red-600 dark:text-red-400">{selectedStudentInfo.progress.Englisch.baseProgress}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Gesamt-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-red-800 dark:text-red-200">{selectedStudentInfo.progress.Englisch.progress}%</span>
                    </div>
                  </div>
                  {selectedStudentInfo.progress.Englisch.bonusTasks > 0 && (
                    <div className="mt-3 p-3 bg-red-100 dark:bg-red-950/50 rounded-lg border border-red-300 dark:border-red-700">
                      <h5 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center">
                        ⭐ Zusatzaufgaben (Bonus)
                      </h5>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Zusatzaufgaben:</span>
                          <span className="font-semibold ml-2">{selectedStudentInfo.progress.Englisch.completedBonusTasks}/{selectedStudentInfo.progress.Englisch.bonusTasks}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Bonus-Fortschritt:</span>
                          <span className="font-semibold ml-2 text-red-600 dark:text-red-400">+{selectedStudentInfo.progress.Englisch.bonusProgress}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Gesamt */}
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                    Gesamt
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Lernpläne gesamt:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Gesamt.learningPlansCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Reguläre Aufgaben:</span>
                      <span className="font-semibold ml-2">{selectedStudentInfo.progress.Gesamt.completedRegularTasks}/{selectedStudentInfo.progress.Gesamt.regularTasks}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Basis-Fortschritt:</span>
                      <span className="font-semibold ml-2 text-green-600 dark:text-green-400">{selectedStudentInfo.progress.Gesamt.baseProgress}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Gesamtfortschritt:</span>
                      <span className="font-semibold ml-2 text-green-800 dark:text-green-200">{selectedStudentInfo.progress.Gesamt.progress}%</span>
                    </div>
                  </div>
                  {selectedStudentInfo.progress.Gesamt.bonusTasks > 0 && (
                    <div className="mt-3 p-3 bg-green-100 dark:bg-green-950/50 rounded-lg border border-green-300 dark:border-green-700">
                      <h5 className="font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center">
                        ⭐ Zusatzaufgaben gesamt (Bonus)
                      </h5>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Zusatzaufgaben:</span>
                          <span className="font-semibold ml-2">{selectedStudentInfo.progress.Gesamt.completedBonusTasks}/{selectedStudentInfo.progress.Gesamt.bonusTasks}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Bonus-Fortschritt:</span>
                          <span className="font-semibold ml-2 text-green-600 dark:text-green-400">+{selectedStudentInfo.progress.Gesamt.bonusProgress}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* KI-Empfehlungen aus Datenbank */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  KI-Empfehlungen
                </h3>
                
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
                  {selectedStudentInfo.kiRecommendations && selectedStudentInfo.kiRecommendations.length > 0 ? (
                    <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-purple-800 dark:text-purple-200 flex items-center">
                          <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          Neueste KI-Empfehlung
                        </h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(selectedStudentInfo.kiRecommendations[0].Datum).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      {selectedStudentInfo.kiRecommendations[0].S && (
                        <div className="mb-3">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status: </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{selectedStudentInfo.kiRecommendations[0].S}</span>
                        </div>
                      )}
                      {selectedStudentInfo.kiRecommendations[0].Memo && (
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Empfehlung: </span>
                          <span dangerouslySetInnerHTML={{
                            __html: selectedStudentInfo.kiRecommendations[0].Memo.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          }} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <p>Keine KI-Empfehlungen für diesen Schüler vorhanden</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowStudentModal(false)}
                className="btn btn-md btn-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      <PersonalDataRequiredModal
        isOpen={showPersonalDataModal}
        onClose={() => {
          setShowPersonalDataModal(false)
          setPendingSendKind(null)
        }}
        onSaved={async () => {
          setShowPersonalDataModal(false)
          const kind = pendingSendKind
          if (!kind) return
          // Retry the exact action
          if (kind === 'raw') await handleSendRawStatistics()
          if (kind === 'interpreted') await handleSendInterpretedStatistics()
        }}
      />

      <EmailCredentialModal
        isOpen={showEmailCredentialModal}
        onClose={() => {
          setShowEmailCredentialModal(false)
          setPendingSendKind(null)
        }}
        credential={null}
        requiredForSending={true}
        onSave={async (credentials) => {
          try {
            const resp = await api.post('/email/credentials', credentials)
            if (resp.data?.success) {
              toast.success('E-Mail-Konto erfolgreich hinzugefügt')
              setShowEmailCredentialModal(false)
              const kind = pendingSendKind
              if (!kind) return
              if (kind === 'raw') await handleSendRawStatistics()
              if (kind === 'interpreted') await handleSendInterpretedStatistics()
            } else {
              toast.error(resp.data?.error || 'Fehler beim Speichern des E-Mail-Kontos')
            }
          } catch (e: any) {
            toast.error(e?.response?.data?.error || e?.response?.data?.message || 'Fehler beim Speichern des E-Mail-Kontos')
          }
        }}
      />
    </div>
  )
}

export default Statistics




